import { CheckCircleIcon } from '@repo/studio-design-system-ui-icons/general/check-circle'
import { getAffiliateIdFromStorage } from '@repo/affiliate-id'
import { usePaymentsBuyCoinsMutation } from '../gen/coins'
import { CoinPack, PaymentStatus } from '@repo/graphql-types'
import { useOptimisticUpdateUserBalance } from './use-optimistic-update-user-balance'
import { useIsMutating } from '@tanstack/react-query'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { toast } from '@repo/studio-components-toast'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useIsCompactViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import {
  openPaymentRequiredDialog as openPaymentRequiredDialogDefault,
  buildStatusDialogsResultFromPaymentStatus,
} from '@repo/studio-hooks-payments/use-payment-required-dialog'
import { toAvailableCurrencyCode, getOneTimePackPriceCents } from '../constants/coin-top-up'

const SHOW_SUCCESS_TIME = 5000
const UPDATE_BALANCE_DELAY = 800

export function usePurchaseCoins(options?: {
  onSuccess?: () => void
  coinPack?: CoinPack
  coinsAmount?: number
  successToastLabel?: string
}) {
  const {
    onSuccess,
    coinPack = CoinPack.CoinPack1000k,
    coinsAmount = 1_000_000,
    successToastLabel,
  } = options ?? {}
  const t = useStudioTranslate()
  const { currency, prices } = usePricesForUser()
  const topUpMutation = usePaymentsBuyCoinsMutation()
  const updateCoinBalance = useOptimisticUpdateUserBalance()
  const isPending = useIsMutating({ mutationKey: ['paymentsBuyCoins'] }) > 0
  const isCompactViewport = useIsCompactViewport()

  const priceCents = getOneTimePackPriceCents(coinPack, prices)

  const onPurchaseSuccess = React.useCallback(() => {
    onSuccess?.()
    sendAmplitudeEvent(AmplitudeEvents.PURCHASED_COIN_PACK, {
      coin_pack: `${coinsAmount} coins`,
      coin_pack_type: 'individual',
      ...(priceCents !== undefined && { price: priceCents }),
    })
    setTimeout(() => updateCoinBalance.adjustUserBalance(coinsAmount), UPDATE_BALANCE_DELAY)
    if (!isCompactViewport) {
      toast({
        position: 'top-center',
        icon: <CheckCircleIcon />,
        title: successToastLabel ?? t('studio.components.coin_top_up_message.toasts.success.title'),
        description: '',
        duration: SHOW_SUCCESS_TIME,
      })
    }
  }, [
    updateCoinBalance,
    isCompactViewport,
    t,
    onSuccess,
    coinsAmount,
    priceCents,
    successToastLabel,
  ])

  const onPurchaseError = React.useCallback(() => {
    toast({
      title: t('studio.components.coin_top_up_message.toasts.error_generic.title'),
      description: t('studio.components.coin_top_up_message.toasts.error_generic.description'),
    })
  }, [t])

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const currencyCode = toAvailableCurrencyCode(currency.code)
      // Forward the captured ref for apps that can't share the affwp_ref cookie
      // cross-domain. On cookie-bearing domains BE reads the cookie directly.
      const affiliateId = getAffiliateIdFromStorage()
      topUpMutation.mutate(
        { coinPack, currencyCode, ...(affiliateId ? { affiliateId } : {}) },
        {
          onSuccess: data => {
            const { errors, paymentStatus, payInvoiceUrl } = data.paymentsBuyCoins
            // Anything other than an explicit `Succeeded` means the coin pack
            // wasn't actually purchased — `Failed` (e.g. STU-7698 invalid card),
            // `RequiresAction` (3DS / SCA challenge with a hosted Stripe URL),
            // `Processing` (async settlement), and any future status — should
            // surface the remediation dialog rather than fire success toasts /
            // optimistic balance bumps that lie to the user. This mirrors the
            // open condition inside `ActivateTrialStatusDialogs` (open whenever
            // `paymentsStatus !== Succeeded`).
            const hasErrors = (errors?.length ?? 0) > 0
            if (hasErrors || paymentStatus !== PaymentStatus.Succeeded) {
              const result = buildStatusDialogsResultFromPaymentStatus(
                paymentStatus,
                payInvoiceUrl ?? null,
              )
              openPaymentRequiredDialogDefault(result)
            } else {
              onPurchaseSuccess()
            }
          },
          onError: onPurchaseError,
        },
      )
    },
    [onPurchaseSuccess, onPurchaseError, currency.code, topUpMutation, coinPack],
  )

  return {
    handleClick,
    isPending,
  }
}
